import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { CartPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/cart-pj/cart-pj.service';
import { ReportCartPjRequest, ReportCartPjResponse } from '@/core/api/sicom-api/registro/prestador/relatorios/cart-pj/cart-pj.model';
import { CartPjJobService } from '@/core/services/cart-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportCartPjRequest;
type Resp = ReportCartPjResponse;

interface CartPjFormControls {
    numero: FormControl<string | null>;
    ano: FormControl<string | null>;
    validade: FormControl<Date | string | null>;
    coordenador: FormControl<string | null>;
}

/**
 * Relatório PDF assíncrono: CART (Certidão de Anotação de Responsabilidade Técnica) PJ.
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui montamos o request
 * a partir do formulário (número, ano, validade, coordenador) e da identificação do prestador
 * (nuCrm/sgUf via `PessoaService`), conforme spec `cart.md`.
 */
@Component({
    selector: 'app-cart-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, InputText, DatePicker, ReactiveFormsModule, RelatorioContainer, CanDirective],
    templateUrl: './cart-pj.html',
    styleUrl: './cart-pj.scss'
})
export class ReportCartPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly formBuilder = inject(FormBuilder);
    private readonly cartService = inject(CartPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly cartJobService = inject(CartPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.cartJobService.job$;

    form: FormGroup<CartPjFormControls> = this.formBuilder.group<CartPjFormControls>({
        numero: new FormControl<string | null>('', Validators.required),
        ano: new FormControl<string | null>('', Validators.required),
        validade: new FormControl<Date | string | null>(null, Validators.required),
        coordenador: new FormControl<string | null>('')
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.cartJobService;
    }

    /** Valida o form e resolve o prestador; retorna null (e avisa) se inválido ou sem CRM/UF. */
    protected buildRequest(): Req | null {
        this.form.markAllAsTouched();
        if (this.form.invalid) return null;

        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a certidão.'
            });
            return null;
        }

        const { numero, ano, validade, coordenador } = this.form.getRawValue();

        return {
            nuCrm,
            sgUf,
            numero: (numero ?? '').trim(),
            ano: (ano ?? '').trim(),
            validade: this.formatarValidade(validade),
            coordenador: (coordenador ?? '').trim() || undefined
        };
    }

    protected exportar(request: Req) {
        return this.cartService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.form.reset({ numero: '', ano: '', validade: null, coordenador: '' });
        this.limparJob();
    }

    /** Formata a validade no padrão `dd/mm/yyyy` exigido pelo backend da certidão. */
    private formatarValidade(value: Date | string | null): string {
        if (!value) return '';
        if (value instanceof Date) {
            const dia = String(value.getDate()).padStart(2, '0');
            const mes = String(value.getMonth() + 1).padStart(2, '0');
            const ano = value.getFullYear();
            return `${dia}/${mes}/${ano}`;
        }
        return String(value);
    }
}
